import type { WeeklyDigestProps } from "./types";

const FONT = "Arial, Helvetica, sans-serif";

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function highlightRow(borderColor: string, label: string, value: string, sub?: string): string {
  return `
  <tr>
    <td width="4" style="background-color:${borderColor};font-size:1px;line-height:1px;border-radius:2px;">&nbsp;</td>
    <td style="padding:0 0 14px 12px;font-family:${FONT};font-size:14px;line-height:20px;color:#374151;vertical-align:top;">
      <strong style="color:#1F3864;">${esc(label)}</strong>${value ? ` — ${esc(value)}` : ""}
      ${sub ? `<div style="color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">${esc(sub)}</div>` : ""}
    </td>
  </tr>`;
}

function actionItem(num: number, text: string): string {
  return `
  <tr>
    <td width="40" style="vertical-align:top;padding:0 12px 12px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="28" height="28" style="background-color:#D4A843;border-radius:14px;">
        <tr><td align="center" valign="middle" style="font-family:${FONT};color:#1F3864;font-size:13px;font-weight:bold;line-height:28px;height:28px;width:28px;">${num}</td></tr>
      </table>
    </td>
    <td style="vertical-align:middle;padding:0 0 12px 0;font-family:${FONT};font-size:14px;line-height:22px;color:#374151;">${esc(text)}</td>
  </tr>`;
}

export function generateWeeklyDigestHTML(p: WeeklyDigestProps): string {
  const isGroup = p.segment === "grouphome";
  const segLabel = p.segment_label || (isGroup ? "inquiries" : "leads");

  // Action items
  const items: string[] = [];
  items.push(`Log in to review your ${p.totalLeads} new ${segLabel} from last week.`);
  if (p.hotLeads > 0) items.push(`Review your ${p.hotLeads} hot ${segLabel} and confirm each one has been contacted.`);
  if (p.staleLeads > 0) items.push(`Assign your ${p.staleLeads} unassigned hot ${segLabel} to a team member immediately.`);
  if (p.emergencyLeads > 0) items.push(`Follow up on ${p.emergencyLeads} emergency lead(s) that scored 100/100 last week.`);
  if (p.teamLogins < p.teamTotal) items.push(`Check in with ${p.teamTotal - p.teamLogins} team member(s) who did not log in last week.`);
  if (p.unreadAlerts > 0) items.push(`Review ${p.unreadAlerts} unresolved platform alert(s) in your dashboard.`);
  items.push(`Send outreach emails to referral sources who have not heard from you in 30+ days.`);

  const actionRows = items.map((t, i) => actionItem(i + 1, t)).join("");

  // Section B intro
  const introB = isGroup
    ? `Your program received ${p.totalLeads} new placement inquiries last week. Here is what stood out.`
    : `Your agency captured ${p.totalLeads} new leads last week. Here is what stood out.`;

  // KPI tile labels
  const tile1Label = isGroup ? "Inquiries Received" : "Leads Captured";
  const tile2Label = isGroup ? "Urgent Inquiries" : "Hot Leads";

  // Highlights
  const row1 = highlightRow(
    "#D4A843",
    isGroup ? "Average Inquiry Score" : "Average Lead Score",
    `${p.avgLeadScore} / 100`
  );
  const row2 = highlightRow("#2E75B6", "Top Referral Source", p.topReferralSource);
  const row3 = highlightRow(
    "#16A34A",
    isGroup ? "Most Active Population" : "Most Active Specialty",
    p.topSpecialty
  );
  const row4 = highlightRow(
    "#7C3AED",
    "Team Activity",
    `${p.teamLogins} of ${p.teamTotal} team members logged in`,
    p.teamLogins < p.teamTotal
      ? `${p.teamTotal - p.teamLogins} team member(s) did not log in. Check the Team Activity report.`
      : undefined
  );

  let row5: string;
  if (p.staleLeads > 0) {
    row5 = highlightRow(
      "#DC2626",
      isGroup ? "Stale Urgent Inquiries" : "Stale Hot Leads",
      isGroup
        ? `${p.staleLeads} urgent inquiry(ies) unassigned for 24+ hours`
        : `${p.staleLeads} hot lead(s) unassigned for 24+ hours`,
      "Assign these leads immediately to prevent lost revenue."
    );
  } else {
    row5 = highlightRow(
      "#16A34A",
      isGroup ? "Inquiry Response" : "Lead Response",
      isGroup
        ? "All urgent inquiries were assigned within 24 hours. Great work."
        : "All hot leads were assigned within 24 hours. Great work."
    );
  }

  // Pipeline card extras for group home
  let groupExtras = "";
  if (isGroup && p.beds_available != null) {
    groupExtras = `
      <tr><td style="padding:14px 0 0 0;"><div style="border-top:1px solid #2E3D55;"></div></td></tr>
      <tr><td align="center" style="padding:14px 0 0 0;">
        <div style="font-family:${FONT};color:#D4A843;font-size:24px;font-weight:bold;line-height:30px;">${esc(p.beds_available)}</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:11px;line-height:16px;">beds available</div>
      </td></tr>
      <tr><td align="center" style="padding:10px 0 0 0;">
        <div style="font-family:${FONT};color:#16A34A;font-size:24px;font-weight:bold;line-height:30px;">${esc(p.placements_this_week ?? 0)}</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:11px;line-height:16px;">placements this week</div>
      </td></tr>`;
  }

  // Emergency tile
  const emergencyTile = p.emergencyLeads > 0
    ? `
    <tr><td style="padding:12px 0 0 0;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FEF2F2;border:1px solid #FECACA;border-radius:8px;">
        <tr><td align="center" style="padding:14px;">
          <div style="font-family:${FONT};color:#DC2626;font-size:32px;font-weight:bold;line-height:38px;">${esc(p.emergencyLeads)}</div>
          <div style="font-family:${FONT};color:#1F3864;font-size:12px;font-weight:bold;line-height:18px;margin-top:4px;">Emergency Leads (Score 100)</div>
          <div style="font-family:${FONT};color:#6B7280;font-size:11px;line-height:16px;margin-top:4px;">These are your highest-priority leads. Follow up immediately if you have not already.</div>
        </td></tr>
      </table>
    </td></tr>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>LegacyGrowth Engine Weekly Digest</title>
</head>
<body style="margin:0;padding:0;background-color:#F0F2F5;font-family:${FONT};">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0F2F5;">
  <tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;">

      <!-- SECTION A: HEADER -->
      <tr><td style="background-color:#0F1E35;padding:32px 40px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td align="left" style="font-family:${FONT};color:#D4A843;font-size:18px;font-weight:bold;line-height:24px;">LegacyGrowth Engine</td>
            <td align="right">
              <span style="background-color:#1F3864;color:#D4A843;font-family:${FONT};font-size:11px;font-weight:bold;padding:4px 10px;border-radius:4px;letter-spacing:1px;">WEEKLY DIGEST</span>
            </td>
          </tr>
          <tr><td colspan="2" style="padding:18px 0 0 0;"><div style="border-bottom:1px solid #2E3D55;"></div></td></tr>
          <tr><td colspan="2" style="padding:18px 0 0 0;font-family:${FONT};color:#FFFFFF;font-size:24px;font-weight:bold;line-height:32px;">Good Monday, ${esc(p.ownerName)}.</td></tr>
          <tr><td colspan="2" style="padding:6px 0 0 0;font-family:${FONT};color:#9AAFC8;font-size:14px;line-height:20px;">Here is your LegacyGrowth Engine summary for ${esc(p.weekRange)}.</td></tr>
        </table>
      </td></tr>

      <!-- SECTION B: KPI STRIP -->
      <tr><td style="background-color:#FFFFFF;padding:32px 40px;">
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:20px;margin-bottom:20px;">${esc(introB)}</div>
        <table role="presentation" cellpadding="0" cellspacing="8" border="0" width="100%" style="border-collapse:separate;border-spacing:8px;">
          <tr>
            <td width="33%" align="center" style="padding:16px;border:1px solid #E5E7EB;border-radius:8px;vertical-align:top;">
              <div style="font-family:${FONT};color:#1F3864;font-size:32px;font-weight:bold;line-height:38px;">${esc(p.totalLeads)}</div>
              <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">${esc(tile1Label)}</div>
            </td>
            <td width="33%" align="center" style="padding:16px;border:1px solid #E5E7EB;border-radius:8px;vertical-align:top;">
              <div style="font-family:${FONT};color:#DC2626;font-size:32px;font-weight:bold;line-height:38px;">${esc(p.hotLeads)}</div>
              <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">${esc(tile2Label)}</div>
            </td>
            <td width="33%" align="center" style="padding:16px;border:1px solid #E5E7EB;border-radius:8px;vertical-align:top;">
              <div style="font-family:${FONT};color:#16A34A;font-size:32px;font-weight:bold;line-height:38px;">${esc(p.callsBooked)}</div>
              <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">Calls Booked</div>
            </td>
          </tr>
        </table>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${emergencyTile}</table>
      </td></tr>

      <!-- SECTION C: HIGHLIGHTS -->
      <tr><td style="background-color:#FFFFFF;border-top:1px solid #E5E7EB;padding:28px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">Last Week's Highlights</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td width="60%" valign="top" style="padding-right:16px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                ${row1}${row2}${row3}${row4}${row5}
              </table>
            </td>
            <td width="40%" valign="top">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#0F1E35;border-radius:8px;">
                <tr><td align="center" style="padding:24px;">
                  <div style="font-family:${FONT};color:#9AAFC8;font-size:11px;font-weight:bold;line-height:16px;letter-spacing:1px;">ESTIMATED PIPELINE VALUE</div>
                  <div style="font-family:${FONT};color:#D4A843;font-size:36px;font-weight:bold;line-height:44px;margin-top:8px;">${esc(p.pipelineValue)}</div>
                  <div style="font-family:${FONT};color:#9AAFC8;font-size:11px;line-height:16px;margin-top:6px;">based on ${esc(p.totalLeads)} leads this week</div>
                  ${groupExtras ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${groupExtras}</table>` : ""}
                </td></tr>
              </table>
            </td>
          </tr>
        </table>
      </td></tr>

      <!-- SECTION D: ACTION ITEMS -->
      <tr><td style="background-color:#FFFBF0;border-top:3px solid #D4A843;padding:28px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">This Week's Action Items</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          ${actionRows}
        </table>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:20px auto 0;">
          <tr><td align="center" style="background-color:#D4A843;border-radius:6px;">
            <a href="${esc(p.ctaUrl)}" style="display:inline-block;padding:14px 28px;font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;text-decoration:none;line-height:20px;">Go to My Dashboard &rarr;</a>
          </td></tr>
        </table>
      </td></tr>

      <!-- SECTION E: FOOTER -->
      <tr><td style="background-color:#0F1E35;padding:24px 40px;">
        <div style="font-family:${FONT};line-height:20px;">
          <span style="color:#D4A843;font-size:14px;font-weight:bold;">LegacyGrowth Engine</span><span style="color:#9AAFC8;font-size:14px;"> &mdash; A product of H&amp;E Associates LLC</span>
        </div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:20px;margin-top:6px;">McLean, Virginia &nbsp;&middot;&nbsp; compliance@heassociatesllc.com</div>
        <div style="margin-top:10px;font-family:${FONT};font-size:12px;line-height:18px;">
          <a href="${esc(p.ctaUrl)}/settings" style="color:#D4A843;text-decoration:underline;">Manage Notifications</a>
          <span style="color:#9AAFC8;"> &middot; </span>
          <a href="${esc(p.unsubscribeUrl)}" style="color:#9AAFC8;text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="font-family:${FONT};color:#4B5563;font-size:11px;line-height:18px;margin-top:12px;">You are receiving this email because you are an active LegacyGrowth Engine subscriber. This email may contain information about leads that include protected health information. Handle in accordance with your HIPAA obligations and your signed Business Associate Agreement with H&amp;E Associates LLC.</div>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}
